// Select at build time; the legacy deployment remains usable until migration.
export const usesHostinger = process.env.REACT_APP_BACKEND === 'hostinger';
let csrfToken = null;

function apiError(detail, status) {
  const error = new Error(detail);
  error.response = { status, data: { detail } };
  return error;
}

async function request(route, { method = 'GET', body, csrf = false } = {}) {
  if (csrf && !csrfToken) await getAdminSession();
  let response;
  try {
    response = await fetch(`/api/index.php?route=${encodeURIComponent(route)}`, {
      method, credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}), ...(csrf ? { 'X-CSRF-Token': csrfToken || '' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
  } catch { throw apiError('Network error. Please try again.'); }
  let payload;
  try { payload = await response.json(); }
  catch { throw apiError('The service returned an invalid response. Please try again.', response.status); }
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) csrfToken = null;
    throw apiError(typeof payload?.detail === 'string' ? payload.detail : 'Request failed. Please try again.', response.status);
  }
  if (typeof payload?.csrf === 'string') csrfToken = payload.csrf;
  return payload;
}

function toAdmin(user) {
  if (!user) return false;
  const meta = { ...(user.app_metadata || {}), ...(user.user_metadata || {}) };
  return { email: user.email, name: meta.name || 'Admin', role: meta.role || 'admin' };
}

export async function getAdminSession() {
  if (usesHostinger) return (await request('session')).user || false;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.auth.getSession();
  if (error) throw apiError(error.message);
  return toAdmin(data.session?.user);
}

export async function watchAdminSession(callback) {
  if (usesHostinger) return () => {};
  const { supabase } = await import('./supabase');
  const { data } = supabase.auth.onAuthStateChange((_event, session) => callback(toAdmin(session?.user)));
  return () => data.subscription.unsubscribe();
}

export async function loginAdmin(email, password) {
  if (usesHostinger) return (await request('login', { method: 'POST', body: { email: email.trim(), password }, csrf: true })).user;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw apiError('Invalid email or password.');
  return toAdmin(data.user);
}

export async function logoutAdmin() {
  if (usesHostinger) {
    await request('logout', { method: 'POST', body: {}, csrf: true });
    csrfToken = null;
    return;
  }
  const { supabase } = await import('./supabase');
  const { error } = await supabase.auth.signOut();
  if (error) throw apiError(error.message);
}

export async function listLeads() {
  if (usesHostinger) {
    const payload = await request('admin/leads');
    if (!Array.isArray(payload.leads)) throw apiError('Could not load leads.');
    return payload.leads;
  }
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase.from('leads').select('*').order('created_at', { ascending: false });
  if (error) throw apiError(error.message);
  return data || [];
}

export async function setLeadStatus(id, status) {
  if (usesHostinger) return request(`admin/leads/${id}`, { method: 'PATCH', body: { status }, csrf: true });
  const { supabase } = await import('./supabase');
  const { error } = await supabase.from('leads').update({ status }).eq('id', id);
  if (error) throw apiError(error.message);
  return { id, status };
}

export async function createLead(data) {
  if (usesHostinger) {
    const lead = await request('leads', { method: 'POST', body: data });
    if (!lead?.id) throw apiError('Your request could not be confirmed. Please try again.');
    return lead;
  }
  const { FUNCTIONS_URL, SUPABASE_ANON } = await import('./supabase');
  let response;
  try {
    response = await fetch(`${FUNCTIONS_URL}/submit-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON, Authorization: `Bearer ${SUPABASE_ANON}` },
      body: JSON.stringify(data),
    });
  } catch { throw apiError('Network error. Please try again.'); }
  let payload;
  try { payload = await response.json(); }
  catch { throw apiError('The service returned an invalid response. Please try again.', response.status); }
  if (!response.ok) throw apiError(payload?.detail || 'Something went wrong. Please try again.', response.status);
  if (!payload?.id) throw apiError('Your request could not be confirmed. Please try again.');
  return payload;
}
